<script lang="ts" setup>
import { useStorage } from "@vueuse/core";
import { NModal, NSelect, NSkeleton, NSwitch, NDataTable, NTag, useMessage } from "naive-ui";
import { computed, h, ref, watch, watchEffect } from "vue";
import { useRoute, useRouter } from "vue-router";
import OpenItemDetail from "../components/open-items/OpenItemDetail.vue";
import OpenItemsTableSkeleton from "../components/open-items/OpenItemsTableSkeleton.vue";
import { useOpenItemsData } from "../hooks/data/useOpenItemsData";
import type { OpenItem, OpenItemComment, OpenItemGroupedStats } from "../models/contador";
import { formatJSDate } from "../utils/dates";
import { isProperMember, stripHtmlTags } from "../utils/strings";

const route = useRoute();
const router = useRouter();
const message = useMessage();

const showResolved = ref(false);

const {
    openItems,
    stats,
    isLoading: loading,
    error,
    isError,
    refetch: refetchOpenItems,
} = useOpenItemsData(showResolved);

watchEffect(() => {
    if (isError.value && !loading.value && error.value) {
        message.error(error.value.message || "Error loading open items...");
    }
});

async function handleRefetchOpenItems() {
    await refetchOpenItems();
}

function onHideModal() {
    const { query, ...rest } = route;
    const newQuery = { ...query } as Record<string, any>;
    delete newQuery["selected-open-item"];
    router.replace({ ...(rest as any), query: newQuery });
}

// Get selected item from URL query
const selectedItemId = computed(() => {
    const id = route.query["selected-open-item"];
    return typeof id === "string" ? id : null;
});

// Group By state via route query
const groupBy = computed(() => {
    const value = route.query.groupBy as string;
    return value || "bank_property";
});

const groupByOptions = [
    { label: "Bank Account + Property", value: "bank_property" },
    { label: "Bank Account", value: "bank" },
    { label: "Property", value: "property" },
    { label: "Direction (PMS/Bank)", value: "direction" },
    { label: "No Grouping", value: "none" },
];

const updateGroupBy = (value: string) => {
    router.replace({
        query: {
            ...route.query,
            groupBy: value === "bank_property" ? undefined : value,
        },
    });
};

// ---- Table logic ----
// Persistent state using useStorage
const collapsedGroupsArray = useStorage<string[]>("open-items-collapsed-groups", []);
const collapsedGroups = computed({
    get: () => new Set(collapsedGroupsArray.value),
    set: (value: Set<string>) => {
        collapsedGroupsArray.value = Array.from(value);
    },
});

const sortOrder = useStorage<"asc" | "desc" | null>("open-items-sort-order", null);
const sortColumn = useStorage<string | null>("open-items-sort-column", null);

// Reset collapsed groups when grouping changes
watch(
    () => groupBy.value,
    () => {
        collapsedGroupsArray.value = [];
    },
);

// Group header row type and helpers
interface GroupHeaderRow {
    isGroupHeader: true;
    propertyName: string;
    itemCount: number;
    totalAmount: number;
    bankAccountName: string;
    bankAccountNumber: string;
    groupKey: string;
}

type TableRow = OpenItem | GroupHeaderRow;

function isGroupHeader(row: TableRow): row is GroupHeaderRow {
    return (row as any).isGroupHeader === true;
}

const toggleGroup = (groupKey: string) => {
    const newCollapsedGroups = new Set(collapsedGroups.value);
    if (newCollapsedGroups.has(groupKey)) newCollapsedGroups.delete(groupKey);
    else newCollapsedGroups.add(groupKey);
    collapsedGroups.value = newCollapsedGroups;
};

function getGroupKey(item: OpenItem, mode: string): string {
    const propertyName = item.property?.name || "Unknown Property";
    const bankAccountName = item.bank_account?.account_name || "Unknown Bank Account";
    const bankAccountNumber = item.bank_account?.account_number?.slice(-4) || "****";
    const source = item.source || "unknown";

    switch (mode) {
        case "bank_property":
            return `${propertyName}|${bankAccountName}|${bankAccountNumber}`;
        case "bank":
            return `${bankAccountName}|${bankAccountNumber}`;
        case "property":
            return propertyName;
        case "direction":
            return source;
        default:
            return "";
    }
}

function getGroupHeaderText(groupKey: string, mode: string): string {
    switch (mode) {
        case "bank_property": {
            const [propertyName, bankAccountName, bankAccountNumber] = groupKey.split("|");
            return `${propertyName} - ${bankAccountName} • ${bankAccountNumber}`;
        }
        case "bank": {
            const [bankAccountName, bankAccountNumber] = groupKey.split("|");
            return `${bankAccountName} • ${bankAccountNumber}`;
        }
        case "property":
            return groupKey;
        case "direction":
            return groupKey === "pms"
                ? "PMS"
                : groupKey === "bank"
                  ? "Bank"
                  : groupKey.toUpperCase();
        default:
            return "";
    }
}

const getSortValue = (item: OpenItem, column: string) => {
    switch (column) {
        case "dueDate": {
            // Use the date field from the API
            return new Date(item.date).getTime();
        }
        case "transactionDate":
            return new Date(item.createdAt).getTime();
        case "amount":
            return item.amount || 0;
        case "source":
            return (item.source || "").toLowerCase();
        case "status":
            return (item.status || "").toLowerCase();
        case "description":
            return (item.description || "").toLowerCase();
        case "comments": {
            const comments = item.comments || [];
            if (comments.length === 0) return 0;
            const latestComment = comments.reduce((latest, current) =>
                new Date(current.createdAt).getTime() > new Date(latest.createdAt).getTime()
                    ? current
                    : latest,
            );
            return new Date(latestComment.createdAt).getTime();
        }
        default:
            return 0;
    }
};

const handleSort = (column: string) => {
    if (sortColumn.value === column) {
        if (sortOrder.value === "asc") sortOrder.value = "desc";
        else if (sortOrder.value === "desc") {
            sortOrder.value = null;
            sortColumn.value = null;
        } else sortOrder.value = "asc";
    } else {
        sortColumn.value = column;
        sortOrder.value = "asc";
    }
};

const createSortableHeader = (columnKey: string, title: string) => {
    return () => {
        const isActive = sortColumn.value === columnKey;
        const isAsc = isActive && sortOrder.value === "asc";
        const isDesc = isActive && sortOrder.value === "desc";

        return h(
            "div",
            {
                class: "flex items-center justify-between cursor-pointer",
                onClick: () => handleSort(columnKey),
            },
            [
                h(
                    "span",
                    {
                        class: isActive ? "text-[#5025C1]" : "",
                    },
                    title,
                ),
                h(
                    "svg",
                    {
                        class: `w-4 h-4 transition-transform duration-200 ${
                            isAsc ? "rotate-180" : isDesc ? "" : "opacity-50"
                        }`,
                        fill: "none",
                        stroke: isActive ? "#5025C1" : "currentColor",
                        viewBox: "0 0 24 24",
                        xmlns: "http://www.w3.org/2000/svg",
                    },
                    [
                        h("path", {
                            "stroke-linecap": "round",
                            "stroke-linejoin": "round",
                            "stroke-width": "2",
                            d: "M19 9l-7 7-7-7",
                        }),
                    ],
                ),
            ],
        );
    };
};

const filteredData = computed<TableRow[]>(() => {
    if (!Array.isArray(openItems.value)) return [];

    const groupByMode = groupBy.value || "bank_property";
    const items = openItems.value;

    if (groupByMode === "none") {
        let sortedItems = [...items];

        if (sortColumn.value && sortOrder.value) {
            sortedItems.sort((a, b) => {
                const aValue = getSortValue(a, sortColumn.value!);
                const bValue = getSortValue(b, sortColumn.value!);

                if (typeof aValue === "string" && typeof bValue === "string") {
                    return sortOrder.value === "asc"
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue);
                }

                return sortOrder.value === "asc"
                    ? (aValue as number) - (bValue as number)
                    : (bValue as number) - (aValue as number);
            });
        }

        return sortedItems;
    }

    // Group items using dynamic grouping key
    const grouped = items.reduce(
        (groups: Record<string, OpenItem[]>, item: OpenItem) => {
            const groupKey = getGroupKey(item, groupByMode);

            if (!groups[groupKey]) groups[groupKey] = [];
            groups[groupKey].push(item);
            return groups;
        },
        {} as Record<string, OpenItem[]>,
    );

    // Sort within each group
    if (sortColumn.value && sortOrder.value) {
        Object.keys(grouped).forEach((groupKey) => {
            grouped[groupKey].sort((a, b) => {
                const aValue = getSortValue(a, sortColumn.value!);
                const bValue = getSortValue(b, sortColumn.value!);

                if (typeof aValue === "string" && typeof bValue === "string") {
                    return sortOrder.value === "asc"
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue);
                }

                return sortOrder.value === "asc"
                    ? (aValue as number) - (bValue as number)
                    : (bValue as number) - (aValue as number);
            });
        });
    }

    // Sort groups by their first item's sort value
    const sortedGroupEntries = Object.entries(grouped);
    if (sortColumn.value && sortOrder.value) {
        sortedGroupEntries.sort(([, aItems], [, bItems]) => {
            if (aItems.length === 0 || bItems.length === 0) return 0;

            const aValue = getSortValue(aItems[0], sortColumn.value!);
            const bValue = getSortValue(bItems[0], sortColumn.value!);

            if (typeof aValue === "string" && typeof bValue === "string") {
                return sortOrder.value === "asc"
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }

            return sortOrder.value === "asc"
                ? (aValue as number) - (bValue as number)
                : (bValue as number) - (aValue as number);
        });
    }

    const flatArray: TableRow[] = [];
    sortedGroupEntries.forEach(([groupKey, propertyItems]) => {
        const displayText = getGroupHeaderText(groupKey, groupByMode);
        const totalAmount = propertyItems.reduce((sum, item) => sum + (item.amount || 0), 0);

        flatArray.push({
            isGroupHeader: true,
            propertyName: displayText,
            itemCount: propertyItems.length,
            totalAmount,
            bankAccountName: "",
            bankAccountNumber: "",
            groupKey,
        } as GroupHeaderRow);

        if (!collapsedGroups.value.has(groupKey)) {
            flatArray.push(...propertyItems);
        }
    });

    return flatArray;
});

const totalColumns = 8;

const columns = computed(() => [
    {
        title: createSortableHeader("dueDate", "DUE DATE"),
        key: "dueDate",
        resizable: true,
        className: sortColumn.value === "dueDate" ? "sort-active" : "",
        colSpan: (row: TableRow) => (isGroupHeader(row) ? totalColumns : 1),
        render: (row: TableRow) => {
            if (isGroupHeader(row)) {
                const isExpanded = !collapsedGroups.value.has((row as GroupHeaderRow).groupKey);
                const gh = row as GroupHeaderRow;
                return h(
                    "div",
                    {
                        class: "group-header-cell font-semibold text-gray-900 bg-[#F2F4F8] px-4 py-3 gap-6 text-base cursor-pointer border-b border-t flex items-center justify-between w-full h-full box-border",
                        onClick: () => toggleGroup(gh.groupKey),
                    },
                    [
                        h("span", { class: "font-semibold text-gray-700" }, gh.propertyName),
                        h("div", { class: "flex items-center gap-2" }, [
                            h(
                                "span",
                                { class: "text-xs text-gray-500 font-normal" },
                                `(${gh.itemCount > 1 ? `${gh.itemCount} items` : `${gh.itemCount} item`})`,
                            ),
                            h(
                                "span",
                                { class: "text-xs text-gray-500 font-normal" },
                                `$${gh.totalAmount.toLocaleString("en-US", { minimumFractionDigits: 2 })}`,
                            ),
                            h(
                                "svg",
                                {
                                    class: `w-4 h-4 transition-transform duration-200 ${
                                        isExpanded ? "rotate-90" : ""
                                    }`,
                                    fill: "none",
                                    stroke: "currentColor",
                                    viewBox: "0 0 24 24",
                                    xmlns: "http://www.w3.org/2000/svg",
                                },
                                [
                                    h("path", {
                                        "stroke-linecap": "round",
                                        "stroke-linejoin": "round",
                                        "stroke-width": "2",
                                        d: "M9 5l7 7-7 7",
                                    }),
                                ],
                            ),
                        ]),
                    ],
                );
            }
            // Use the date field from the API
            const dueDate = new Date((row as OpenItem).due_date);
            const now = new Date();
            const resetDueDate = new Date(dueDate.setHours(0, 0, 0, 0));
            const resetNow = new Date(now.setHours(0, 0, 0, 0));
            const timeDiff = resetDueDate.getTime() - resetNow.getTime();
            const daysUntil = Math.ceil(timeDiff / (1000 * 3600 * 24));
            let dueDateText = "";
            if (daysUntil === 0) {
                dueDateText = formatJSDate(dueDate, { include_year: false }).NUMERIC_DATE_FORMAT;
            } else if (daysUntil < 0) {
                dueDateText = `(${Math.abs(daysUntil)} days past due)`;
            } else {
                dueDateText = `(${daysUntil} days until due)`;
            }
            return h(
                "div",
                {
                    class: "text-sm flex w-24 max-w-32 flex-col justify-center items-start gap-1 self-stretch",
                },
                [
                    h(
                        "div",
                        { class: "text-xs" },
                        daysUntil === 0
                            ? dueDateText
                            : formatJSDate(dueDate, { include_year: false }).NUMERIC_DATE_FORMAT,
                    ),
                    (row as OpenItem).status === "open" && daysUntil !== 0
                        ? h(
                              "div",
                              {
                                  class: "text-xs",
                                  style: { color: daysUntil < 0 ? "#ef4444" : "#6b7280" },
                              },
                              dueDateText,
                          )
                        : null,
                ].filter(Boolean),
            );
        },
    },
    {
        title: createSortableHeader("transactionDate", "Tx. DATE"),
        key: "transactionDate",
        resizable: true,
        className: sortColumn.value === "transactionDate" ? "sort-active" : "",
        render: (row: TableRow) => {
            if (isGroupHeader(row)) return null;
            return h(
                "div",
                {
                    class: "text-sm flex w-24 h-[62px] max-w-32 justify-between items-center",
                },
                formatJSDate(new Date((row as OpenItem).date), { include_year: true })
                    .NUMERIC_DATE_FORMAT,
            );
        },
    },
    {
        title: createSortableHeader("description", "DESCRIPTION"),
        key: "description",
        width: 250,
        resizable: true,
        className: sortColumn.value === "description" ? "sort-active" : "",
        render: (row: TableRow) => {
            if (isGroupHeader(row)) return null;
            return h(
                "div",
                {
                    class: "font-semibold text-gray-600 line-clamp-2 overflow-hidden leading-tight",
                },
                (row as OpenItem).description,
            );
        },
    },
    {
        title: createSortableHeader("amount", "AMOUNT"),
        key: "amount",
        resizable: true,
        minWidth: 140,
        className: sortColumn.value === "amount" ? "sort-active" : "",
        render: (row: TableRow) => {
            if (isGroupHeader(row)) return null;
            const amount = (row as OpenItem).amount || 0;
            const colorClass = amount > 0 ? "text-[#238F6B]" : amount < 0 ? "text-red" : "";
            return h(
                "span",
                {
                    class: `text-sm font-semibold text-right flex  items-center self-stretch ${colorClass}`,
                },
                `$${amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}`,
            );
        },
    },
    {
        title: "BANK ACCOUNT",
        key: "bankAccount",
        resizable: true,
        render: (row: TableRow) => {
            if (isGroupHeader(row)) return null;
            const r = row as OpenItem;
            return h(
                "div",
                {
                    class: "text-sm flex flex-col justify-center items-start min-w-[140px] flex-[0_0_140px]",
                },
                [
                    h(
                        "div",
                        {
                            class: "font-medium text-xs overflow-hidden text-ellipsis whitespace-nowrap w-full text-left",
                        },
                        r.bank_account?.account_name || "N/A",
                    ),
                    h(
                        "div",
                        { class: "text-gray-500 text-xs" },
                        `*** ${r.bank_account?.account_number?.slice(-4) || "0000"}`,
                    ),
                ],
            );
        },
    },
    {
        title: createSortableHeader("source", "SOURCE"),
        key: "source",
        resizable: true,
        className: sortColumn.value === "source" ? "sort-active" : "",
        render: (row: TableRow) => {
            if (isGroupHeader(row)) return null;
            return h(
                "div",
                {
                    class: "text-sm flex w-[120px] min-w-[120px] max-w-[120px] h-8 items-center overflow-hidden text-ellipsis whitespace-nowrap",
                },
                (row as OpenItem).source || "N/A",
            );
        },
    },
    {
        title: createSortableHeader("status", "STATUS"),
        key: "status",
        resizable: true,
        className: sortColumn.value === "status" ? "sort-active" : "",
        render: (row: TableRow) => {
            if (isGroupHeader(row)) return null;
            const r = row as OpenItem;
            return h(
                "div",
                {
                    class: "flex min-w-[120px] justify-start",
                },
                [
                    h(
                        NTag,
                        {
                            type:
                                r.status === "open"
                                    ? "warning"
                                    : r.status === "resolved"
                                      ? "success"
                                      : "default",
                            size: "small",
                            round: false,
                        },
                        { default: () => r.status?.toUpperCase() || "UNKNOWN" },
                    ),
                ],
            );
        },
    },
    {
        title: createSortableHeader("comments", "COMMENTS"),
        key: "comments",
        resizable: true,
        className: sortColumn.value === "comments" ? "sort-active" : "",
        render: (row: TableRow) => {
            if (isGroupHeader(row)) return null;

            const comments: OpenItemComment[] = (row as OpenItem).comments || [];
            const commentCount = comments.length;

            if (commentCount === 0) {
                return h(
                    "div",
                    {
                        class: "flex items-center flex-[0_0_100px] min-w-[180px] text-gray-400",
                    },
                    "No comments",
                );
            }

            const latestComment = comments.reduce((latest, current) =>
                new Date(current.createdAt).getTime() > new Date(latest.createdAt).getTime()
                    ? current
                    : latest,
            );

            return h(
                "div",
                {
                    class: "flex items-center flex-[0_0_100px] min-w-[180px]",
                },
                [
                    h(
                        "div",
                        {
                            class: "min-w-6 min-h-6 bg-gray-600 rounded-full flex items-center justify-center text-white text-xs font-medium",
                        },
                        (latestComment.author || "?")[0].toUpperCase(),
                    ),
                    h(
                        "div",
                        {
                            class: `ml-2 flex flex-col gap-1 max-w-full flex-1 ${(() => {
                                const { isProper } = isProperMember(latestComment.author || "");
                                return isProper ? "font-bold" : "";
                            })()}`,
                        },
                        [
                            h(
                                "div",
                                {
                                    class: "text-xs text-gray-600 line-clamp-2 overflow-hidden leading-tight",
                                },
                                stripHtmlTags(latestComment.content) || "No text",
                            ),
                        ],
                    ),
                    commentCount > 1
                        ? h(
                              "div",
                              {
                                  class: "ml-2 text-xs text-gray-400 font-medium border border-gray-400 rounded-md px-1",
                              },
                              `+${commentCount - 1}`,
                          )
                        : null,
                ].filter(Boolean),
            );
        },
    },
]);

function rowProps(row: TableRow) {
    if (isGroupHeader(row)) {
        return {
            class: "group-header-row relative h-12 font-semibold bg-gray-100",
        } as any;
    }
    return {
        class: "bg-white cursor-pointer transition-all duration-200 ease-in-out",
        onClick: () => {
            const r = row as OpenItem;
            router.push({
                path: route.path,
                query: {
                    ...route.query,
                    "selected-open-item": r.id,
                },
            });
        },
    } as any;
}
</script>

<template>
    <main class="open-items-page">
        <h1 class="mx-10 pt-8 text-2xl font-semibold">Reconciliations</h1>

        <!-- Inline Stats  -->
        <div class="py-4 mx-10 mt-4">
            <div class="flex items-center gap-4 flex-wrap h-fit">
                <div
                    class="border border-gray-300 rounded-lg px-[8px] py-[8px] flex items-center gap-3 bg-white"
                >
                    <!-- Loading State with Skeleton -->
                    <template v-if="loading">
                        <!-- Open Items Skeleton -->
                        <div class="flex items-center gap-2">
                            <div class="w-4 h-4 bg-orange-400 rounded-full"></div>
                            <n-skeleton text class="h-4 w-20" />
                        </div>
                        <!-- Separator -->
                        <div class="bg-gray-300 w-[1px] h-4"></div>
                        <!-- Past Due Skeleton -->
                        <div class="flex items-center gap-2">
                            <div class="w-4 h-4 bg-red-400 rounded-full"></div>
                            <n-skeleton text class="h-4 w-16" />
                        </div>
                        <!-- Separator -->
                        <div class="bg-gray-300 w-[1px] h-4"></div>
                        <!-- New Messages Skeleton -->
                        <div class="flex items-center gap-2">
                            <div class="w-4 h-4 bg-purple-500 rounded-full"></div>
                            <n-skeleton text class="h-4 w-24" />
                        </div>
                    </template>

                    <!-- Stats Display -->
                    <template v-else-if="stats">
                        <!-- Open Items -->
                        <div class="flex items-center gap-2">
                            <div class="w-4 h-4 bg-orange-400 rounded-full"></div>
                            <span class="stats-label"
                                >{{ (stats as OpenItemGroupedStats).open_count }} OPEN ITEMS</span
                            >
                        </div>
                        <!-- Separator -->
                        <div class="bg-gray-300 w-[1px] h-4"></div>
                        <!-- Past Due -->
                        <div class="flex items-center gap-2">
                            <div class="w-4 h-4 bg-red-400 rounded-full"></div>
                            <span class="stats-label"
                                >{{ (stats as OpenItemGroupedStats).past_due_count }} PAST DUE</span
                            >
                        </div>
                        <!-- Separator -->
                        <div class="bg-gray-300 w-[1px] h-4"></div>
                        <!-- New Messages -->
                        <div class="flex items-center gap-2">
                            <div class="w-4 h-4 bg-purple-500 rounded-full"></div>
                            <span class="stats-label"
                                >{{ (stats as OpenItemGroupedStats).new_messages_count }} NEW
                                MESSAGES</span
                            >
                        </div>
                    </template>

                    <!-- No Data State -->
                    <div v-else class="flex items-center gap-2">
                        <span class="stats-label">No stats available</span>
                    </div>
                </div>

                <!-- Group By Dropdown -->
                <div class="flex items-center gap-2">
                    <span class="stats-label text-nowrap !text-[10px] text-left">Group By</span>
                    <n-select
                        class="min-w-[200px] text-left"
                        :value="groupBy"
                        :options="groupByOptions"
                        @update:value="updateGroupBy"
                    />
                </div>

                <!-- Show Resolved Toggle -->
                <div class="flex items-center gap-2">
                    <n-switch
                        :value="showResolved"
                        size="small"
                        @update:value="(v: boolean) => (showResolved = v)"
                    />
                    <label>Show Resolved</label>
                </div>
            </div>
        </div>

        <!-- Error State -->
        <div v-if="error" class="flex-1 flex justify-center items-center mx-10">
            <div class="bg-red-50 border border-red-200 rounded-lg p-6">
                <h3 class="text-lg font-medium text-red-800 mb-2">Error Loading Open Items</h3>
                <p class="text-red-600">
                    {{ error.message || "Failed to load open items" }}
                </p>
            </div>
        </div>

        <!-- Loading State with Skeleton -->
        <open-items-table-skeleton v-if="loading" class="mt-8 sm:mt-0" />

        <!-- Loaded State: Inline Table -->
        <div v-else class="open-items-table mx-2 md:mx-10 overflow-x-auto mt-2 sm:mt-0 md:mt-8">
            <n-data-table
                :columns="columns"
                :data="filteredData"
                :row-props="rowProps"
                :bordered="true"
                :single-line="false"
                :single-column="false"
                size="small"
                class="min-w-max rounded-lg border border-gray-300"
            />
        </div>

        <!-- Modal -->
        <n-modal
            :show="!!selectedItemId"
            preset="card"
            class="p-open-items-modal"
            :style="{
                width: '100%',
                maxWidth: '1100px',
                height: '95vh',
                position: 'fixed',
                left: '50%',
                bottom: 0,
                transform: 'translateX(-50%)',
                borderRadius: '16px 16px 0 0',
            }"
            close-on-esc
            @update:show="onHideModal"
        >
            <open-item-detail
                v-if="selectedItemId"
                :open-item-id="selectedItemId"
                @refetch-open-items="handleRefetchOpenItems"
                @close="onHideModal"
            />
            <button @click="handleRefetchOpenItems">Refetch Open Items</button>
        </n-modal>
    </main>
</template>

<style>
.open-items-page {
    background: linear-gradient(180deg, #eeeafb 0%, rgba(255, 255, 255, 0) 145px);
}

.n-card.n-modal.p-open-items-modal > .n-card-header {
    display: none;
}

.n-card.n-modal.p-open-items-modal > .n-card__content {
    padding: 0;
    height: 100%;
}

/* Stats label styles */
.stats-label {
    color: #1d1c3d;
    font-family: "Basier Circle Mono", ui-monospace, sans-serif;
    font-size: 10px;
    font-style: normal;
    font-weight: 500;
    line-height: 130%;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    font-feature-settings:
        "liga" off,
        "clig" off;
}

/* Table styles (scoped to .open-items-table wrapper) */
.open-items-table .group-header-row .n-data-table-td {
    /* @apply p-0; */
    padding: 0 !important;
}

.open-items-table .n-data-table-wrapper {
    /* @apply rounded-lg; */
    border-radius: 0.5rem;
}

.open-items-table .n-data-table {
    /* @apply text-sm rounded-lg overflow-hidden; */
    font-size: 0.875rem; /* text-sm */
    border-radius: 0.5rem;
    overflow: hidden;
}

.open-items-table .n-data-table-td {
    /* @apply py-2 px-3 border-0; */
    padding: 0.5rem 0.75rem;
    border: 0 !important;
    border-left: none !important;
    border-right: none !important;
}

.open-items-table .n-data-table-td:first-child {
    /* @apply border-r border-gray-200; */
    border-right: 1px solid #e5e7eb !important;
}

.open-items-table .n-data-table-th {
    /* @apply py-3 px-3 bg-gray-50 font-semibold text-xs text-gray-600 uppercase tracking-wider border-0; */
    padding: 0.75rem 0.75rem;
    background-color: #f9fafb;
    font-weight: 600;
    font-size: 0.75rem;
    color: #4b5563;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border: 0 !important;
    border-left: none !important;
    border-right: none !important;
}

.open-items-table .n-data-table-th:first-child {
    /* @apply border-r border-gray-200; */
    border-right: 1px solid #e5e7eb !important;
}

.open-items-table .n-data-table-th.sort-active {
    background-color: #f2f4f8 !important;
}

/* Round the top corners of the header */
.open-items-table .n-data-table-thead .n-data-table-tr:first-child .n-data-table-th:first-child {
    border-top-left-radius: 0.5rem;
}

.open-items-table .n-data-table-thead .n-data-table-tr:first-child .n-data-table-th:last-child {
    border-top-right-radius: 0.5rem;
}

/* Round the bottom corners of the last row */
.open-items-table .n-data-table-tbody .n-data-table-tr:last-child .n-data-table-td:first-child {
    border-bottom-left-radius: 0.5rem;
}

.open-items-table .n-data-table-tbody .n-data-table-tr:last-child .n-data-table-td:last-child {
    border-bottom-right-radius: 0.5rem;
}

.open-items-table .n-data-table-tbody .n-data-table-tr:hover {
    background-color: #f9fafb;
}

.open-items-table .n-data-table-expand {
    background-color: #ffffff !important;
    border-top: 1px solid #e5e7eb;
}
</style>
